import * as React from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

type FilterConfig = {
  debounce?: number;
};

type Config<K extends string> = Record<K, FilterConfig>;
type Values<K extends string> = Partial<Record<K, string>>;

export type TableFilters<K extends string> = {
  values: Values<K>;
  applied: Values<K>;
  set: (name: K, value: string) => void;
  clear: () => void;
  hasAny: boolean;
};

export function useTableFilters<K extends string>(
  config: Config<K>,
): TableFilters<K> {
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const keys = React.useMemo(() => Object.keys(config) as K[], [config]);

  const readFromParams = React.useCallback((): Values<K> => {
    const out: Values<K> = {};
    for (const k of keys) {
      const v = searchParams.get(k);
      if (v) out[k] = v;
    }
    return out;
  }, [keys, searchParams]);

  const [local, setLocal] = React.useState<Values<K>>(readFromParams);

  React.useEffect(() => {
    setLocal((prev) => {
      const next = readFromParams();
      let changed = false;
      for (const k of keys) {
        if ((prev[k] ?? "") !== (next[k] ?? "")) {
          changed = true;
          break;
        }
      }
      return changed ? next : prev;
    });
  }, [searchParams]);

  const timers = React.useRef<
    Partial<Record<K, ReturnType<typeof setTimeout>>>
  >({});

  const writeToUrl = React.useCallback(
    (next: Values<K>) => {
      const sp = new URLSearchParams(searchParams.toString());
      for (const k of keys) {
        const v = next[k];
        if (v) sp.set(k, v);
        else sp.delete(k);
      }
      const qs = sp.toString();
      nav(qs ? `${pathname}?${qs}` : pathname, { replace: true });
    },
    [keys, searchParams, pathname, nav],
  );

  const set = React.useCallback(
    (name: K, value: string) => {
      const trimmed = value;
      setLocal((prev) => {
        const next = { ...prev, [name]: trimmed };
        if (!trimmed) delete next[name];
        const debounceMs = config[name]?.debounce ?? 0;
        const t = timers.current[name];
        if (t) clearTimeout(t);
        if (debounceMs > 0) {
          timers.current[name] = setTimeout(() => writeToUrl(next), debounceMs);
        } else {
          queueMicrotask(() => writeToUrl(next));
        }
        return next;
      });
    },
    [config, writeToUrl],
  );

  const clear = React.useCallback(() => {
    Object.values(timers.current).forEach(
      (t) => t && clearTimeout(t as ReturnType<typeof setTimeout>),
    );
    timers.current = {};
    setLocal({});
    nav(pathname, { replace: true });
  }, [pathname, nav]);

  React.useEffect(() => {
    const t = timers.current;
    return () => {
      Object.values(t).forEach(
        (x) => x && clearTimeout(x as ReturnType<typeof setTimeout>),
      );
    };
  }, []);

  const applied = React.useMemo(() => readFromParams(), [readFromParams]);
  const hasAny = Object.values(applied).some((v) => Boolean(v));

  return { values: local, applied, set, clear, hasAny };
}
